import type { AuditableEntity } from "../types";

export type ConnectorType =
  | "twilio"
  | "heyup"
  | "sip"
  | "web"
  | "temporary"
  | "meta"
  | "email";

// Maximum caller numbers a "temporary" (demo) connector may declare.
export const TEMPORARY_CALLER_NUMBERS_MAX = 5;
export type ConnectorStatus = "active" | "inactive" | "error";
/**
 * What a connector can carry. `voice` is a telephone line (numbers, provider
 * credentials); `chat` is the web channel, and since web audio it covers both
 * typing and speaking in the browser ("Appeler ici"), gated by
 * `configuration.web_call_enabled`. Mirrors the backend's ConnectorCapability.
 */
export type ConnectorCapability =
  | "sms"
  | "whatsapp"
  | "voice"
  | "chat"
  | "messenger"
  | "instagram"
  | "email";

/**
 * Everything that is true of a connector *because of its type*. Mirrors the
 * backend's `CONNECTOR_TYPES` (entities/sessions-connectors.ts) — the fields the
 * UI needs, plus its own display label. Keep the two in step: they are the same
 * table, and the API is the only thing between them.
 *
 * One table rather than a `type === "…"` in each component, so that adding a
 * type is one edit the compiler checks. Genuinely type-specific *form* fields
 * (a Twilio account id, a demo's caller numbers) stay in the form.
 */
export interface ConnectorTypeSpec {
  /** How the type is named to the user. */
  label: string;
  /** Capabilities a connector of this type may declare. */
  capabilities: ConnectorCapability[];
  /** What the create form ticks when the user has chosen nothing. */
  defaultCapabilities: ConnectorCapability[];
  /**
   * Does `configuration.phone_numbers` list numbers this tenant may *present*
   * — as a handset caller ID, say? False for the web channel, which has none,
   * and for a demo, which lists the numbers allowed to call *in* on a line
   * belonging to someone else: presenting one would be claiming their number.
   */
  ownsNumbers: boolean;
}

export const CONNECTOR_TYPES: Record<ConnectorType, ConnectorTypeSpec> = {
  twilio: {
    label: "Twilio",
    capabilities: ["sms", "whatsapp", "voice"],
    // WhatsApp is available but off by default: its sender has to be registered
    // per business before it carries anything.
    defaultCapabilities: ["sms", "voice"],
    ownsNumbers: true,
  },
  // A HeyUp number is a Twilio number in *our* account, so the client never
  // sees a credential. WhatsApp stays out: its sender is registered per
  // business on the account that owns the number, which is ours — paperwork the
  // client has no way to do.
  heyup: {
    label: "HeyUp",
    capabilities: ["voice", "sms"],
    defaultCapabilities: ["voice", "sms"],
    ownsNumbers: true,
  },
  // HeyUp registered as an extension on the client's own PBX. Its calls are
  // identified by that registration, and a number typed here is proven by
  // nothing — so it lends no caller ID (the backend says the same).
  sip: {
    label: "SIP",
    capabilities: ["voice"],
    defaultCapabilities: ["voice"],
    ownsNumbers: false,
  },
  web: {
    label: "Web",
    capabilities: ["chat"],
    defaultCapabilities: ["chat"],
    ownsNumbers: false,
  },
  // One WhatsApp number, Facebook Page or Instagram account connected through
  // the client's own Meta login. Created by the sign-in flow, never by the form
  // (the backend refuses a payload), and it carries text only.
  meta: {
    label: "Meta",
    capabilities: ["whatsapp", "messenger", "instagram"],
    defaultCapabilities: [],
    ownsNumbers: false,
  },
  // A demo tenant borrows the line's owner for everything that line does —
  // calls and texts alike. WhatsApp stays out: it needs a sender registered per
  // business, which is the paperwork a demo exists to skip.
  temporary: {
    label: "Temporary",
    capabilities: ["voice", "sms"],
    defaultCapabilities: ["voice", "sms"],
    ownsNumbers: false,
  },
  // A mailbox of the client's own: read over IMAP, answered over SMTP. No
  // number, no provider account of ours.
  email: {
    label: "E-mail",
    capabilities: ["email"],
    defaultCapabilities: ["email"],
    ownsNumbers: false,
  },
};

/** The spec for a type, or null when the API sent one this build predates. */
export const connectorTypeSpec = (
  type: string | null | undefined,
): ConnectorTypeSpec | null => CONNECTOR_TYPES[type as ConnectorType] ?? null;

/** How to name a connector type to the user, unknown ones included. */
export const connectorTypeLabel = (type: string): string =>
  connectorTypeSpec(type)?.label ?? type;

// Matrix defining which capabilities are available for each connector type
export const CONNECTOR_CAPABILITIES = Object.fromEntries(
  (Object.keys(CONNECTOR_TYPES) as ConnectorType[]).map((type) => [
    type,
    CONNECTOR_TYPES[type].capabilities,
  ]),
) as Record<ConnectorType, ConnectorCapability[]>;

/** The demo line's own numbers — where to call/text to reach the agent. */
export interface DemoDialNumbers {
  voice: string | null;
  sms: string | null;
}

/**
 * Whether HeyUp is registered on the client's PBX — server-computed, `sip`
 * connectors only, from what Asterisk last reported (backend
 * `connectors/pbx-status.ts`).
 */
export interface PbxRegistrationState {
  state: "connected" | "connecting" | "rejected" | "unreachable" | "unknown";
  /** Epoch ms of the last change. */
  since?: number;
  /** Epoch ms of the last report. */
  checked_at?: number;
}

export interface Connector extends AuditableEntity {
  client_id: string;
  type: ConnectorType;
  status: ConnectorStatus;
  capabilities: ConnectorCapability[];
  agent_id?: string; // Optional: specific agent for this connector
  configuration: ConnectorConfiguration;
  /** Server-computed, active `sip` connectors only. */
  registration?: PbxRegistrationState;
  /**
   * Server-computed, `temporary` connectors only: the numbers to dial to reach
   * this tenant's agent. They belong to the demo line's owner, not to this
   * tenant, so they are not in `configuration`.
   */
  demo_numbers?: DemoDialNumbers;
}

// Caller-based routing: rules matched (in order) against the incoming caller
// number. The first match decides how the call is handled.
export type CallRoutingAction = "agent" | "transfer" | "reject";

/**
 * When a destination counts as reachable. Absent = `{ kind: "always" }`, which
 * is what every destination configured before this existed reads as.
 * Mirrors DestinationAvailability in the backend connector entity.
 */
export type DestinationAvailability =
  | { kind: "always" }
  | { kind: "member_schedule" } // "member" destinations: the member's own hours
  | { kind: "schedule"; schedule_id: string };

/**
 * Where a call goes when it leaves the agent.
 *
 * "device" is a handset we provisioned — we know its address and whether it is
 * even plugged in. "sip" is a raw address on a PBX that is not ours, dialled
 * as-is. Mirrors CallDestination in the backend connector entity.
 */
export type CallDestination = (
  | { kind: "member"; member_id: string }
  | { kind: "user"; user_id: string }
  | { kind: "phone"; number: string }
  | { kind: "device"; device_id: string }
  | { kind: "sip"; uri: string }
) & {
  availability?: DestinationAvailability;
  label?: string;
};

export type CallDestinationKind = CallDestination["kind"];

export interface CallRoutingRule {
  id: string; // stable id (used to key rows in the UI)
  pattern: string; // e.g. "+34*" (trailing "*" = prefix) or an exact number
  action: CallRoutingAction;
  agent_id?: string; // action === "agent"
  /**
   * action === "transfer": where the call goes, in order. Destinations we can
   * rule out before dialling (closed hours, unplugged handset) are skipped, and
   * a leg that rings out hands over to the next.
   */
  transfer_destinations?: CallDestination[];
  // Superseded by transfer_destinations, still honoured by the backend when it
  // is absent.
  transfer_member_id?: string; // action === "transfer": a team member (user id)
  transfer_number?: string; // action === "transfer": a raw phone number
  label?: string; // optional note
}

// Geographic filtering of inbound calls, matched on the caller's country
// (reported by the carrier, or derived from the number's calling code).
// Evaluated after the routing rules: an explicit rule always wins.
export type GeoFilterMode = "allow" | "block";

export interface GeoCallFilter {
  enabled: boolean;
  mode: GeoFilterMode;
  /** ISO 3166-1 alpha-2 codes. */
  countries: string[];
  /** Accept calls whose country cannot be determined (withheld caller ID). */
  allow_unknown?: boolean;
}

/** Default allow-list: France and the European countries bordering it. */
export const FRANCE_BORDERING_COUNTRIES = [
  "FR",
  "BE",
  "LU",
  "DE",
  "CH",
  "IT",
  "ES",
  "MC",
  "AD",
];

export const defaultGeoFilter = (): GeoCallFilter => ({
  enabled: false,
  mode: "allow",
  countries: [...FRANCE_BORDERING_COUNTRIES],
  allow_unknown: false,
});

// Which countries this line may call OUT to. Unlike the inbound filter above it is
// never "off": absent means the default policy — the line's own country and nothing
// else — because an outbound leash nobody enabled is discovered on an invoice.
export interface OutboundCallFilter {
  /**
   * Calling-code prefixes reachable ON TOP of the line's own country, which is always
   * allowed and is never in this list. Prefixes rather than ISO codes: that is what the
   * carrier bills on, and "+1809" can open a slice of "+1" without opening all of it.
   */
  prefixes: string[];
  /** Everywhere. For a tenant whose business genuinely is international. */
  allow_all?: boolean;
}

export const defaultOutboundFilter = (): OutboundCallFilter => ({
  prefixes: [],
  allow_all: false,
});

/** One row of the backend's calling-code table (`/connectors/calling-codes`). */
export interface CallingCode {
  /** "+32" */
  code: string;
  /** ISO 3166-1 alpha-2, resolved to a name against `assets/countries.json`. */
  country: string;
}

/** How our Asterisk reaches the client's PBX. */
export type SipTransportProtocol = "udp" | "tcp" | "tls";

/**
 * A `sip` connector: the extension HeyUp registers as on the client's PBX
 * (backend `connectors/pbx-registration.ts`). The password is never returned;
 * an edit that leaves it out keeps the stored one.
 */
export interface SipRegistration {
  server: string;
  port?: number;
  transport?: SipTransportProtocol;
  username: string;
  auth_username?: string;
  password?: string;
  /** Digits the PBX wants before an outside number ("0", "9"). */
  outbound_prefix?: string;
  /** `national` (06…, 0049… abroad) or `e164` (+33…). Absent = national. */
  number_format?: SipNumberFormat;
}

export type SipNumberFormat = "national" | "e164";

/** The Meta account behind a `meta` connector. The token never reaches the browser. */
export type MetaChannel = "whatsapp" | "messenger" | "instagram";

export interface MetaConnectorConfiguration {
  channel: MetaChannel;
  account_id: string;
  waba_id?: string;
  page_id?: string;
  /** Verified business name, Page name or @handle. */
  display_name?: string;
  /** Meta refused our token: the account has to be connected again. */
  needs_reconnect?: boolean;
  coexistence?: boolean;
}

/**
 * One side of a mailbox. The password is never returned; an edit that leaves
 * it out keeps the stored one. Mirrors the backend's `MailServer`.
 */
export interface MailServer {
  host: string;
  port?: number;
  /** TLS from the first byte (465, 993). Off: STARTTLS is required. */
  secure?: boolean;
  username: string;
  password?: string;
}

/** An `email` connector's mailbox (backend `EmailAccount`). */
export interface EmailAccount {
  address: string;
  display_name?: string;
  smtp: MailServer;
  imap: MailServer & { mailbox?: string };
  /** Set by the server on the first save; mail older than this is never answered. */
  listen_since?: number;
  /** Copy each answer into the Sent folder. Absent = yes, except where the provider does it itself. */
  save_to_sent?: boolean;
  /** The Sent folder when the server does not flag it. */
  sent_folder?: string;
}

export interface ConnectorConfiguration {
  /** `meta` connectors only. */
  meta?: MetaConnectorConfiguration;

  /** `email` connectors only. */
  email_account?: EmailAccount;

  /** `sip` connectors only. */
  sip_registration?: SipRegistration;

  // Universal fields for all connector types
  phone_numbers: string[];
  whatsapp_numbers: string[];

  // Twilio-specific fields
  account_id: string;
  auth_token: string;

  /**
   * Twilio Region the account's numbers are homed in ("ie1", "au1", …); empty
   * means the default "us1". The API host has to match the number's own routing
   * configuration, or Twilio refuses the message with "'From' phone number
   * routing configuration is incorrect".
   */
  twilio_region?: string;

  // Caller-based routing rules (optional)
  routing_rules?: CallRoutingRule[];

  // Geographic filtering of inbound calls (optional)
  geo_filter?: GeoCallFilter;

  // Which countries this line may call out to (see OutboundCallFilter). Absent is NOT
  // "no filtering": it is the default policy, the line's own country only.
  outbound_filter?: OutboundCallFilter;

  // Where the caller is sent when we cannot serve the call — the agent failed
  // to start, or no inbound audio ever arrived. Ordered: the first reachable
  // destination takes the call, and a leg that fails hands over to the next.
  fallback_destinations?: CallDestination[];

  // While this schedule is open the agent does not pick up: inbound calls go
  // straight down `fallback_destinations`. Empty/absent = always the agent.
  agent_off_schedule_id?: string;

  // Seconds the team rings on a call sent straight to it (agent-off window,
  // "transfer" rule) before the agent takes it. 0/absent = no hand-over.
  ring_before_agent_seconds?: number;

  // Superseded by fallback_destinations, still honoured by the backend when it
  // is absent.
  fallback_destination?: CallDestination;

  // Superseded by fallback_destination, still honoured by the backend when it
  // is absent so connectors configured before the SIP option keep working.
  fallback_member_id?: string;
  fallback_number?: string;

  // Web connector: optional allow-list of origins/hostnames that may open the embed.
  allowed_origins?: string[];

  // Web connector: HMAC secret for authenticated in-app identities (read-only in UI).
  identity_secret?: string;

  // Shared demo numbers.
  // On a twilio/sip connector: this number is shared for demos — an inbound call
  // is handed over to the tenant that registered the caller number, and falls
  // back to this connector's agent (the instructions) when nobody matches.
  // SYSADMIN-only.
  demo_number?: boolean;
  // On a demo number: its agent asks callers for a demo space's code and puts them
  // through to that space (`transfer_to_agent_by_code`). SYSADMIN-only.
  code_routing?: boolean;
  // On a "temporary" connector: the caller numbers this tenant will demo from.
  caller_numbers?: string[];

  // This connector answers on every number *no* connector owns — a number not
  // attributed to a client yet, a client removed, a line moved. Platform-wide
  // and SYSADMIN-only, like demo_number.
  default_connector?: boolean;

  // Voice connector: offer "call here" (in-browser web audio to the agent) in the chat.
  web_call_enabled?: boolean;

  // Web connector: let the tenant's own website ask, unauthenticated, for a visitor to
  // be called back. Off unless someone turned it on — this connector id is already
  // published in every embed snippet, so a permissive default would turn all of them
  // into a way to make the line dial an arbitrary number.
  public_callback_enabled?: boolean;
}

/**
 * The numbers HeyUp has attributed to this account, i.e. what a `heyup`
 * connector may answer on. `enabled` is false when the deployment has no HeyUp
 * carrier account configured at all, in which case the option is not offered.
 */
export interface HeyupNumbers {
  numbers: string[];
  enabled: boolean;
}

export interface CreateConnectorData {
  type: ConnectorType;
  status?: ConnectorStatus;
  capabilities?: ConnectorCapability[];
  agent_id?: string;
  configuration: Partial<ConnectorConfiguration>;
}

export interface UpdateConnectorData {
  type?: ConnectorType;
  agent_id?: string;
  status?: ConnectorStatus;
  capabilities?: ConnectorCapability[];
  configuration?: Partial<ConnectorConfiguration>;
}

export interface TestConnectorResult {
  success: boolean;
  message: string;
}
